import {
  RadioGroup,
  Radio as MuiRadio,
  FormControlLabel,
  Box,
  StyledEngineProvider,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import * as CommonIcons from 'assets/common';
import { forwardRef } from 'react';
import {
  Control,
  FieldPath,
  FieldValues,
  PathValue,
  RegisterOptions,
  useController,
  UseControllerProps,
  Validate,
} from 'react-hook-form';
import { classNames, getDataPropsFromRest } from 'utilities/Utils';
import Typography from '../Typegraphy/Typography';
type OptionsProps = {
  label: React.ReactNode;
  value: string | number;
  description?: React.ReactNode;
  rightComponent?: React.ReactNode;
  disabled?: boolean;
};
type CommonProps = {
  options: OptionsProps[];
  disabled?: boolean;
  color?: 'primary' | 'secondary' | 'default';
  size?: 'small' | 'medium';
  inline?: boolean;
  className?: string;
  variant?: 'default' | 'boxed' | 'descriptive';
  selectedOptionSubComp?: (value: string) => React.ReactElement;
  marginBottomNone?: boolean;
  groupClassName?: string;
  selectedClassName?: string;
};
type RadioPropsType = CommonProps & {
  name: string;
  onChange: (value: string, name: string) => void;
  value: string;
};
type VariantStyleProps = {
  variant: 'default' | 'boxed' | 'descriptive';
  option: OptionsProps;
  index: number;
};
type ExtraRadioPropsType = {
  icon?: React.ReactNode;
  checkedIcon?: React.ReactNode;
  disableRipple?: boolean;
};

type StylesPropType = {
  variant: 'default' | 'boxed' | 'descriptive';
  marginBottomNone: boolean;
  inline: boolean;
};

const useStyles = makeStyles(() => ({
  radioGroupRoot: (props: StylesPropType) => ({
    gap: `${props.inline ? '16px' : '0px'}`,
  }),
  formRoot: (props: StylesPropType) => ({
    marginBottom: props.marginBottomNone && 0,
    marginLeft: 0,
    marginRight: 0,
  }),
  root: (props: StylesPropType) => ({
    '&:hover': {
      backgroundColor: 'transparent !important',
    },
    padding: `4px ${props.variant === 'default' ? '11px' : '0px'} 4px 0px`,
  }),

  boxContainer: {
    width: '384px',
    border: '2px solid #DEDEDE',
    borderRadius: '8px',
    padding: '12px',
    marginLeft: '2px',
    display: 'grid',
    gridTemplateColumns: '24px auto',
  },
  icon: {
    height: '20px',
    width: '20px',
  },
  active: {
    borderColor: '#3543BF',
  },
  optionContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    textAlign: 'start',
  },
  icon1: {
    borderRadius: '50%',
    width: 14,
    height: 14,
    backgroundColor: '#fff',
    border: '1px solid #787878',
    '$root.Mui-focusVisible &': {
      outline: '2px auto rgba(19,124,189,.6)',
      outlineOffset: 2,
    },
    'input:hover ~ &': {
      backgroundColor: '#fff',
    },
    'input:disabled ~ &': {
      boxShadow: 'none',
      background: '#EFEFEF',
      border: '1px solid #D2D1D1',
    },
  },
  checkedIcon: {
    backgroundColor: '#3543BF',
    border: 'none',
    backgroundImage:
      'linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))',
    '&:before': {
      display: 'block',
      width: 14,
      height: 14,
      backgroundImage: 'radial-gradient(#fff,#fff 28%,transparent 32%)',
      content: '""',
    },
    'input:hover ~ &': {
      backgroundColor: '#3543BF',
    },
    'input:disabled ~ &': {
      border: 'none',
      background: '#D2D1D1',
    },
    'input:disabled ~ &:before': {
      backgroundImage: 'radial-gradient(#EFEFEF,#EFEFEF 28%,transparent 32%)',
    },
  },
  boxedLabel: {
    borderRadius: '4px',
    backgroundColor: '#fff',
    border: 'solid 1px #b7b7b7',
    color: '#b7b7b7',
    fontSize: '12px',
    height: '38px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '85px',
    padding: '0px 15px',
    'input:hover ~ &': {
      background: 'rgba(183, 183, 183, 0.15)',
      color: '#212121',
    },
    'input:disabled ~ &': {
      background: 'rgba(206, 217, 224, 0.15)',
    },
  },
  checkedBoxedLabel: {
    border: 'solid 1px #fd7e32',
    color: '#fd7e32',
    'input:hover ~ &': {
      color: '#fe7d32',
      border: '1px solid #fe7d32',
      background: 'rgba(254, 125, 50, 0.15)',
    },
  },
}));

function RadioComp(props: Partial<RadioPropsType>, ref) {
  const {
    name,
    onChange,
    value,
    options,
    disabled = false,
    color = 'primary',
    size = 'medium',
    inline,
    className,
    variant = 'default',
    selectedOptionSubComp = () => {},
    marginBottomNone = false,
    groupClassName = '',
    selectedClassName = '',
    ...rest
  } = props;
  const handleChange = event => {
    if (disabled) {
      return;
    }
    onChange(event.target.value, event.target.name);
  };
  const {
    boxContainer,
    active,
    icon,
    root,
    optionContainer,
    icon1,
    checkedIcon,
    formRoot,
    boxedLabel,
    checkedBoxedLabel,
    radioGroupRoot,
  } = useStyles({ variant, marginBottomNone, inline });
  const dataTestIdProps = getDataPropsFromRest(rest);

  const getStylesForVariant = ({
    variant: radioVariant,
    option,
    index,
  }: VariantStyleProps): ExtraRadioPropsType => {
    const extraRadioProps: ExtraRadioPropsType = {};
    if (radioVariant === 'descriptive') {
      extraRadioProps.icon = (
        <Box className={classNames(boxContainer)}>
          <CommonIcons.EmptyRadio className={icon} />
          <Box className={optionContainer}>
            <Typography fb inline>
              {option.description ? option.description : `Option ${index + 1}`}
            </Typography>
            <Box>{option.label}</Box>
          </Box>
        </Box>
      );
      extraRadioProps.checkedIcon = (
        <Box className={classNames(boxContainer, active)}>
          <CommonIcons.CheckedRadio className={icon} />
          <Box className={optionContainer}>
            <Typography inline>
              {option.description ? option.description : `Option ${index + 1}`}
            </Typography>
            <Box>{option.label}</Box>
          </Box>
        </Box>
      );
      extraRadioProps.disableRipple = true;
    } else if (radioVariant === 'boxed') {
      extraRadioProps.icon = (
        <span className={classNames(boxedLabel)}>{option.label}</span>
      );
      extraRadioProps.checkedIcon = (
        <span className={classNames(boxedLabel, checkedBoxedLabel)}>
          {option.label}
        </span>
      );
      extraRadioProps.disableRipple = true;
    } else {
      extraRadioProps.icon = <span className={classNames(icon1)} />;
      extraRadioProps.checkedIcon = (
        <span className={classNames(icon1, checkedIcon)} />
      );
      extraRadioProps.disableRipple = true;
    }
    return extraRadioProps;
  };

  return (
    <StyledEngineProvider injectFirst>
      <RadioGroup
        value={`${value}`}
        {...dataTestIdProps}
        ref={ref}
        row={inline}
        className={`${classNames(radioGroupRoot)} ${groupClassName}`}
      >
        {options.map((option, index) => {
          const extraRadioProps: ExtraRadioPropsType = getStylesForVariant({
            variant,
            option,
            index,
          });
          return (
            <>
              <Box>
                <FormControlLabel
                  key={index}
                  label={
                    variant === 'default' && (
                      <div className="flex">
                        <Typography
                          variant="body1"
                          themeColor={`${
                            disabled ? 'text.disable' : 'text.primary'
                          }`}
                          className={classNames(
                            option.value === value && selectedClassName,
                          )}
                        >
                          {option.label}
                        </Typography>
                        {option?.rightComponent}
                      </div>
                    )
                  }
                  classes={{ root: formRoot }}
                  value={`${option.value}`}
                  onChange={handleChange}
                  disabled={option.disabled}
                  className={className}
                  control={
                    <MuiRadio
                      name={name}
                      disabled={disabled}
                      color={color}
                      size={size}
                      disableRipple
                      classes={{ root, checked: root }}
                      {...extraRadioProps}
                    />
                  }
                />
                {option.description && (
                  <Box className="ml-6 -mt-1">
                    <Typography variant="subtitle1" themeColor="text.secondary">
                      {option.description}
                    </Typography>
                  </Box>
                )}
              </Box>
              {value === option.value && selectedOptionSubComp?.(value)}
            </>
          );
        })}
      </RadioGroup>
    </StyledEngineProvider>
  );
}
const Radio = forwardRef(RadioComp);
type ControlledRadioProps<
  T extends FieldValues,
  TName extends FieldPath<T> = FieldPath<T>,
> = CommonProps & {
  name: TName;
  control: Control<T, unknown>;
  defaultValue?: UseControllerProps<T, TName>['defaultValue'];
  isRequired?: boolean;
  customValidator?: Validate<PathValue<T, TName>, T>;
};
function ControlledRadio<
  T extends FieldValues,
  TName extends FieldPath<T> = FieldPath<T>,
>(props: ControlledRadioProps<T, TName>) {
  const {
    name,
    control,
    defaultValue = null,
    isRequired = false,
    customValidator,
    ...additional
  } = props;
  const validations: Partial<RegisterOptions<T, TName>> = {
    required: {
      value: isRequired,
      message: 'This field is required',
    },
  };
  if (typeof customValidator === 'function') {
    validations.validate = customValidator;
  }
  const {
    field: { onChange, value, ref },
  } = useController<T, TName>({
    name,
    rules: validations,
    defaultValue,
    control,
  });
  return (
    <StyledEngineProvider injectFirst>
      <Radio
        ref={ref}
        value={value}
        onChange={radioValue => onChange(radioValue)}
        {...additional}
      />
    </StyledEngineProvider>
  );
}
export { Radio, ControlledRadio };
